import React, { forwardRef } from 'react';

import { Flexbox, FontAwesomeIcon } from '@schoolinks/ui-components';

import cn from 'classnames';

import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';

import CircularProgress from '@mui/material/CircularProgress';

import v from 'components/variables';

import s from './Button.css';

const colors = {
  primary: v.green,
  secondary: v.blue,
  error: '#EC6D6E',
  secondaryDark: '#0288D1',
};

const defaultStyle = {
  borderRadius: 20,
  boxSizing: 'content-box',
  padding: '4px 15px',
  minWidth: 0,
  fontSize: 16,
  lineHeight: '24px',
};

const Button = (props, ref) => {
  const {
    className,
    style,
    variant,
    color,
    disabled,
    children,
    iconLeft,
    iconRight,
    whiteBackground,
    leftArrow = false,
    rightArrow = false,
    leftBoldArrow = false,
    rightBoldArrow = false,
    loading = false,
    compact = false,
    loadingMessage = '',
    ...restProps
  } = props;

  const transparent = whiteBackground ? '#fff' : 'transparent';
  const borderColor = disabled ? '#d8d8d8' : colors[color] || '#757575';
  const bgColor =
    (variant === 'filled' && borderColor) ||
    (variant === 'outlined' && transparent) ||
    transparent;
  const textColor =
    (variant === 'filled' && '#fff') ||
    (variant === 'outlined' && borderColor) ||
    '#757575';

  const leftIcon =
    (leftArrow && (
      <FontAwesomeIcon
        className={cn(s.chevron, s.leftChevron)}
        icon={faChevronRight}
        rotation={180}
      />
    )) ||
    (leftBoldArrow && (
      <FontAwesomeIcon className={s.boldArrowIcon} icon={faArrowRight} />
    )) ||
    iconLeft ||
    null;

  const rightIcon =
    (rightArrow && (
      <FontAwesomeIcon
        className={cn(s.chevron, s.rightChevron)}
        icon={faChevronRight}
      />
    )) ||
    (rightBoldArrow && (
      <FontAwesomeIcon className={s.boldArrowIcon} icon={faArrowRight} />
    )) ||
    iconRight ||
    null;

  return (
    <button
      type="button"
      ref={ref}
      className={cn(className, s.button, {
        [s.redFill]: color === 'error' && variant === 'outlined',
        [s.greenFill]: color === 'primary' && variant === 'outlined',
        [s.greyFill]: !color && variant === 'outlined',
        [s.blueFill]: color === 'secondary' && variant === 'outlined',
        [s.darkBlueFill]: color === 'secondaryDark' && variant === 'outlined',
        [s.whiteFill]: variant === 'filled',
        [s.disabledFill]: disabled && variant === 'outlined',
        [s.leftArrow]: leftArrow,
        [s.rightArrow]: rightArrow,
        [s.leftBoldArrow]: leftBoldArrow,
        [s.rightBoldArrow]: rightBoldArrow,
        [s.compact]: Boolean(compact),
      })}
      style={{
        ...defaultStyle,
        border: `solid 2px ${borderColor}`,
        backgroundColor: bgColor,
        color: textColor,
        ...style,
      }}
      disabled={disabled}
      {...restProps}
    >
      {loading ? (
        <Flexbox alignItems="center">
          <CircularProgress
            size={20}
            style={{
              color: `${textColor}`,
            }}
            className={cn({ [s.spinner]: loadingMessage })}
          />
          {loadingMessage}
        </Flexbox>
      ) : (
        <>
          {leftIcon}
          {children}
          {rightIcon}
        </>
      )}
    </button>
  );
};

export default forwardRef(Button);
